import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Box, Snackbar, SnackbarContent, Typography } from '@mui/material';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { APP_LINK_STATE, NAV_ROUTES } from '../Home/navRoutes';
import { useAuth } from '../../core/authContext';
import { registerLeaveGuard } from '../../core/leaveGuard';
import { saveThresholds, type Thresholds } from '../../core/thresholds';
import { DiscardDialog } from './DiscardDialog';
import { CRITERIA_DISCARD_BODY, ExitCriteriaTab } from './ExitCriteriaTab';
import { YEARS_DISCARD_BODY, YearsTab } from './YearsTab';
import { SETTINGS_EXIT_CRITERIA_ROUTE, SETTINGS_PLANS_ROUTE, SETTINGS_YEARS_ROUTE, type SettingsTab } from './routes';
import { PlansTab } from './PlansTab';
import {
  confirmExitCriteria,
  confirmYears,
  fetchExitCriteria,
  fetchPlanSettings,
  fetchYears,
  saveExitCriteria,
  savePlanSignOffRule,
  saveYears,
  type ExitCriteriaSettings,
  type GradeBand,
  type PlanSettings,
  type PlanSignOffRule,
  type ReviewedConfirmation,
  type SetupState,
  type YearsSettings,
} from './settingsApi';
import { SETTINGS } from './tokens';
import { PAGE_MAX_WIDTH } from '../../theme';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

export type SettingsSources = {
  exitCriteria: () => Promise<ExitCriteriaSettings>;
  confirmExitCriteria: (reviewed: ReviewedConfirmation) => Promise<SetupState>;
  saveExitCriteria: (gradeBands: GradeBand[], reviewed: ReviewedConfirmation) => Promise<ExitCriteriaSettings>;
  years: () => Promise<YearsSettings>;
  confirmYears: (reviewed: Thresholds) => Promise<YearsSettings>;
  saveYears: (values: Thresholds) => Promise<YearsSettings>;
  cacheThresholds: (values: Thresholds) => boolean;
  planSettings: () => Promise<PlanSettings>;
  savePlanSignOffRule: (rule: PlanSignOffRule) => Promise<PlanSettings>;
};

const defaultSources: SettingsSources = {
  exitCriteria: fetchExitCriteria,
  confirmExitCriteria,
  saveExitCriteria,
  years: fetchYears,
  confirmYears,
  saveYears,
  cacheThresholds: saveThresholds,
  planSettings: fetchPlanSettings,
  savePlanSignOffRule,
};

const TABS: { id: SettingsTab; label: string; route: string }[] = [
  { id: 'exit-criteria', label: 'Exit criteria', route: SETTINGS_EXIT_CRITERIA_ROUTE },
  { id: 'years', label: 'Newcomer and long-term years', route: SETTINGS_YEARS_ROUTE },
  { id: 'plans', label: 'Plans', route: SETTINGS_PLANS_ROUTE },
];

export function SettingsPage({ tab, sources = defaultSources }: { tab: SettingsTab; sources?: SettingsSources }) {
  const navigate = useNavigate();
  const { isSuperAdmin, impersonation } = useAuth();
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingLeave, setPendingLeave] = useState<{ proceed: () => void } | null>(null);
  const [toast, setToast] = useState<{ key: number; message: string } | null>(null);
  const dirtyRef = useRef(false);
  const deferredLeave = useRef<{ proceed: () => void } | null>(null);

  const onDirtyChange = useCallback((next: boolean) => {
    dirtyRef.current = next;
    setDirty(next);
  }, []);

  const onSavingChange = useCallback((next: boolean) => {
    setSaving(next);
    if (next) return;
    const deferred = deferredLeave.current;
    if (deferred === null) return;
    deferredLeave.current = null;
    if (dirtyRef.current) setPendingLeave(deferred);
    else deferred.proceed();
  }, []);

  const requestLeave = useCallback(
    (proceed: () => void) => {
      if (saving) deferredLeave.current = { proceed };
      else if (dirty) setPendingLeave({ proceed });
      else proceed();
    },
    [dirty, saving]
  );

  useEffect(() => registerLeaveGuard(requestLeave), [requestLeave]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const onToast = useCallback((message: string) => setToast({ key: Date.now(), message }), []);

  const discard = () => {
    const leave = pendingLeave;
    setPendingLeave(null);
    onDirtyChange(false);
    leave?.proceed();
  };

  if (isSuperAdmin && !impersonation) return <Navigate to={NAV_ROUTES.impersonationAdmin} replace />;

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: SETTINGS.surface }}>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="settings" />
      <Box component="main" sx={{ flex: 1, minWidth: 0, padding: '32px 24px' }}>
        <Box sx={{ maxWidth: PAGE_MAX_WIDTH, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography component="h1" sx={{ fontFamily: SETTINGS.font, fontSize: 28, lineHeight: '36px', fontWeight: 700, color: SETTINGS.ink }}>
              Settings
            </Typography>
            <Typography sx={{ fontFamily: SETTINGS.font, fontSize: 16, lineHeight: '24px', color: SETTINGS.muted }}>
              How your district defines its ML program. Plans, reports, and the dashboard all use these.
            </Typography>
          </Box>
          <Box role="tablist" sx={{ display: 'flex', gap: '24px', borderBottom: `1px solid ${SETTINGS.divider}` }}>
            {TABS.map((t) => {
              const selected = t.id === tab;
              return (
                <Box
                  key={t.id}
                  component="button"
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => {
                    if (!selected) requestLeave(() => navigate(t.route, { state: APP_LINK_STATE }));
                  }}
                  sx={{
                    mb: '-1px',
                    py: '8px',
                    px: 0,
                    border: 0,
                    borderBottom: `2px solid ${selected ? SETTINGS.primary : 'transparent'}`,
                    bgcolor: 'transparent',
                    color: selected ? SETTINGS.primary : SETTINGS.muted,
                    fontFamily: SETTINGS.font,
                    fontSize: 16,
                    lineHeight: '24px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    '&:hover': { color: selected ? SETTINGS.primary : SETTINGS.ink },
                  }}
                >
                  {t.label}
                </Box>
              );
            })}
          </Box>
          {tab === 'exit-criteria' ? (
            <ExitCriteriaTab
              source={sources.exitCriteria}
              confirm={sources.confirmExitCriteria}
              save={sources.saveExitCriteria}
              onToast={onToast}
              onDirtyChange={onDirtyChange}
              onSavingChange={onSavingChange}
              requestLeave={requestLeave}
            />
          ) : tab === 'plans' ? (
            <PlansTab source={sources.planSettings} save={sources.savePlanSignOffRule} onToast={onToast} />
          ) : (
            <YearsTab
              years={sources.years}
              confirm={sources.confirmYears}
              save={sources.saveYears}
              cacheThresholds={sources.cacheThresholds}
              onToast={onToast}
              onDirtyChange={onDirtyChange}
              onSavingChange={onSavingChange}
              requestLeave={requestLeave}
            />
          )}
        </Box>
      </Box>
      <DiscardDialog
        open={pendingLeave !== null}
        body={tab === 'exit-criteria' ? CRITERIA_DISCARD_BODY : YEARS_DISCARD_BODY}
        onKeep={() => setPendingLeave(null)}
        onDiscard={discard}
      />
      {toast && (
        <Snackbar
          key={toast.key}
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          autoHideDuration={6000}
          onClose={(_, reason) => reason !== 'clickaway' && setToast(null)}
        >
          <SnackbarContent role="status" message={toast.message} sx={{ bgcolor: SETTINGS.primaryDark, color: '#FFFFFF', borderRadius: '4px' }} />
        </Snackbar>
      )}
    </Box>
  );
}
